import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import SelectDropDown from "@/components/Common/SelectDropDown";
import { PICKER_STYLES } from "@/helpers/pickerStyles";
import { useLeadRouting, useSaveLeadRouting } from "@/hooks/useBrokerageManage";
import "./LeadRouting.scss";
import LockedRow from "@/components/LockedRow";
import RoutingExplainer from "@/components/RoutingExplainer";

export const MODE_OPTIONS = [
  { value: "rotate", label: "Take turns, each lead goes to the next agent" },
  { value: "area", label: "By area, the ZIP codes you set go to one agent" },
  { value: "price", label: "By the buyer's budget" },
  { value: "visitor", label: "The visitor picks the agent" },
];

const digitsOf = (value) => String(value ?? "").replace(/\D/g, "");

export const draftOf = (data) => ({
  mode: data?.mode || "rotate",
  areaRules: (data?.areaRules || []).map((rule) => ({ zips: rule.zips.join(", "), userId: rule.userId })),
  priceRules: (data?.priceRules || []).map((rule) => ({
    min: rule.min === null || rule.min === undefined ? "" : String(rule.min),
    max: rule.max === null || rule.max === undefined ? "" : String(rule.max),
    userId: rule.userId,
  })),
});

export const payloadOf = (draft) => ({
  mode: draft.mode,
  areaRules: draft.areaRules.map((rule) => ({
    zips: rule.zips.split(/[\s,]+/).filter(Boolean),
    userId: rule.userId,
  })),
  priceRules: draft.priceRules.map((rule) => ({
    min: digitsOf(rule.min) ? Number(digitsOf(rule.min)) : null,
    max: digitsOf(rule.max) ? Number(digitsOf(rule.max)) : null,
    userId: rule.userId,
  })),
});

const ERROR_LINES = {
  zips: "Enter five-digit ZIP codes separated by commas.",
  range: "Enter a lowest budget, a highest budget, or both, with the lowest first.",
  userId: "Pick an agent who is on your page.",
};

const errorFor = (errors, kind, index, field) => (errors?.[`${kind}.${index}.${field}`] ? ERROR_LINES[field] : "");

const ROW_ERROR = /^(areaRules|priceRules)\.(\d+)\.(.+)$/;

// Row errors are keyed by position, so drop or shift them when rows change.
const withoutRowError = (errors, kind, index, shift) =>
  Object.fromEntries(
    Object.entries(errors || {}).flatMap(([key, value]) => {
      const match = ROW_ERROR.exec(key);
      if (!match || match[1] !== kind) return [[key, value]];
      const at = Number(match[2]);
      if (at < index) return [[key, value]];
      if (at === index) return [];
      return [[shift ? `${kind}.${at - 1}.${match[3]}` : key, value]];
    }),
  );

const hasShownError = (fields) =>
  Object.keys(fields || {}).some((key) => key === "areaRules" || key === "priceRules" || ROW_ERROR.test(key));

const AgentPicker = ({ id, agents, value, onChange }) => {
  const options = agents.map((agent) => ({ value: agent.userId, label: agent.name }));
  return (
    <SelectDropDown
      id={id}
      className="lead-routing__agent"
      classNamePrefix="select"
      placeholder="Agent"
      options={options}
      value={options.find((option) => option.value === value) || null}
      styles={PICKER_STYLES}
      errorClass="d-none"
      onChange={(option) => option && onChange(option.value)}
    />
  );
};

const LeadRouting = ({ brokerageId, onSeePlan }) => {
  const query = useLeadRouting(brokerageId);
  const save = useSaveLeadRouting(brokerageId);
  const data = query.data;
  const [draft, setDraft] = useState(() => draftOf(data));
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (data && !data.locked) setDraft(draftOf(data));
  }, [JSON.stringify(data)]);

  if (!data) {
    return (
      <section className="content-block lead-routing">
        <div className="title-sec">
          <h4>Leads from your website</h4>
        </div>
        <p className="lead-routing__state" aria-busy={!query.isError}>
          {query.isError ? "Lead routing did not load." : "Loading lead routing…"}
        </p>
      </section>
    );
  }

  if (data.locked) {
    return (
      <section className="content-block lead-routing">
        <div className="title-sec">
          <h4>Leads from your website</h4>
        </div>
        <RoutingExplainer mode="rotate" crm={Boolean(data.crmForward)} />
        <LockedRow
          id="lead-routing"
          title="Route by area or budget"
          body={"Website leads take turns across your agents today. Send each one to the agent who covers that area or budget with the brokerage\u00a0plan."}
          actionLabel="See the brokerage plan"
          onAction={onSeePlan}
          dismissible={false}
          requireFirstListing={false}
        />
      </section>
    );
  }

  const agents = data.agents || [];
  const dirty = JSON.stringify(payloadOf(draft)) !== JSON.stringify(payloadOf(draftOf(data)));
  const setRule = (kind, index, patch) => {
    setErrors((current) => withoutRowError(current, kind, index, false));
    setDraft({ ...draft, [kind]: draft[kind].map((rule, at) => (at === index ? { ...rule, ...patch } : rule)) });
  };
  const addRule = (kind, blank) => setDraft({ ...draft, [kind]: [...draft[kind], blank] });
  const removeRule = (kind, index) => {
    setErrors((current) => withoutRowError(current, kind, index, true));
    setDraft({ ...draft, [kind]: draft[kind].filter((_, at) => at !== index) });
  };

  const submit = () => {
    setErrors({});
    save.mutate(payloadOf(draft), {
      onSuccess: () => toast.success("Lead routing is saved."),
      onError: (error) => {
        if (error.fields) setErrors(error.fields);
        if (!hasShownError(error.fields))
          toast.error(
            error.fields?.plus
              ? "Start the brokerage plan to route leads by area or budget."
              : error.fields
                ? "Lead routing was not saved. Check your rules and try again."
                : error.message,
          );
      },
    });
  };

  return (
    <section className="content-block lead-routing">
      <div className="title-sec">
        <h4>Leads from your website</h4>
      </div>
      <RoutingExplainer mode={draft.mode} crm={Boolean(data.crmForward)} />
      <div className="lead-routing__mode">
        <span className="lead-routing__label">Send each lead</span>
        <SelectDropDown
          id="lead-routing-mode"
          className="lead-routing__select"
          classNamePrefix="select"
          options={MODE_OPTIONS}
          value={MODE_OPTIONS.find((option) => option.value === draft.mode)}
          styles={PICKER_STYLES}
          errorClass="d-none"
          onChange={(option) => option && setDraft({ ...draft, mode: option.value })}
        />
      </div>

      {draft.mode === "area" ? (
        <ul className="lead-routing__rules">
          {draft.areaRules.map((rule, index) => (
            <li key={`area-${index}`} className="lead-routing__rule">
              <input
                className="lead-routing__input lead-routing__zips"
                value={rule.zips}
                inputMode="numeric"
                aria-label={`ZIP codes for area ${index + 1}`}
                aria-invalid={Boolean(errorFor(errors, "areaRules", index, "zips"))}
                placeholder="79401, 79407"
                onChange={(event) => setRule("areaRules", index, { zips: event.target.value })}
              />
              <AgentPicker
                id={`lead-routing-area-${index}`}
                agents={agents}
                value={rule.userId}
                onChange={(userId) => setRule("areaRules", index, { userId })}
              />
              <button type="button" className="lead-routing__link" onClick={() => removeRule("areaRules", index)}>
                Remove
              </button>
              {errorFor(errors, "areaRules", index, "zips") || errorFor(errors, "areaRules", index, "userId") ? (
                <span className="lead-routing__error" role="alert">
                  {errorFor(errors, "areaRules", index, "zips") || errorFor(errors, "areaRules", index, "userId")}
                </span>
              ) : null}
            </li>
          ))}
          <li className="lead-routing__rule is-rest">Any other ZIP code takes turns</li>
        </ul>
      ) : null}

      {draft.mode === "price" ? (
        <ul className="lead-routing__rules">
          {draft.priceRules.map((rule, index) => (
            <li key={`price-${index}`} className="lead-routing__rule">
              <input
                className="lead-routing__input lead-routing__price"
                value={rule.min}
                inputMode="numeric"
                aria-label={`Lowest budget for range ${index + 1}`}
                placeholder="Lowest $"
                onChange={(event) => setRule("priceRules", index, { min: event.target.value })}
              />
              <input
                className="lead-routing__input lead-routing__price"
                value={rule.max}
                inputMode="numeric"
                aria-label={`Highest budget for range ${index + 1}`}
                placeholder="Highest $"
                onChange={(event) => setRule("priceRules", index, { max: event.target.value })}
              />
              <AgentPicker
                id={`lead-routing-price-${index}`}
                agents={agents}
                value={rule.userId}
                onChange={(userId) => setRule("priceRules", index, { userId })}
              />
              <button type="button" className="lead-routing__link" onClick={() => removeRule("priceRules", index)}>
                Remove
              </button>
              {errorFor(errors, "priceRules", index, "range") || errorFor(errors, "priceRules", index, "userId") ? (
                <span className="lead-routing__error" role="alert">
                  {errorFor(errors, "priceRules", index, "range") || errorFor(errors, "priceRules", index, "userId")}
                </span>
              ) : null}
            </li>
          ))}
          <li className="lead-routing__rule is-rest">Any other budget, or no budget, takes turns</li>
        </ul>
      ) : null}

      {draft.mode === "area" || draft.mode === "price" ? (
        <button
          type="button"
          className="lead-routing__link lead-routing__add"
          onClick={() =>
            draft.mode === "area"
              ? addRule("areaRules", { zips: "", userId: null })
              : addRule("priceRules", { min: "", max: "", userId: null })
          }
        >
          {draft.mode === "area" ? "Add an area" : "Add a budget range"}
        </button>
      ) : null}
      {errors.areaRules || errors.priceRules ? (
        <p className="lead-routing__error" role="alert">
          Add at least one {draft.mode === "area" ? "area" : "budget range"}, or pick another way to send&nbsp;leads.
        </p>
      ) : null}

      {dirty ? (
        <div className="lead-routing__actions">
          <button type="button" className="lead-routing__link" onClick={() => setDraft(draftOf(data))}>
            Discard
          </button>
          <button type="button" className="btn btn-primary lead-routing__save" disabled={save.isPending} onClick={submit}>
            {save.isPending ? "Saving…" : "Save lead routing"}
          </button>
        </div>
      ) : null}
    </section>
  );
};

export default LeadRouting;
